// src/components/modals/ViewGRNModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { X, Package, FileText, Building, User, Calendar, Truck } from 'lucide-react'
import OrangeLoadingSpinner from '../ui/OrangeLoadingSpinner'
import GRNService from '../../services/grnService'
import BranchService from '../../services/branchService'
import SuppliersService from '../../services/suppliersService'
import BatchInventoryService from '../../services/batchInventoryService'
import { GRNModel } from '../../utils/grnModel'

const ViewGRNModal = ({ grn, onClose }) => {
  const [grnDetails, setGRNDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [branchInfo, setBranchInfo] = useState(null)
  const [supplierInfo, setSupplierInfo] = useState(null)
  const [additionalDataLoading, setAdditionalDataLoading] = useState(false)
  const [expiryDates, setExpiryDates] = useState({}) // Store expiry dates by item ID
  const [expiryLoading, setExpiryLoading] = useState(false)

  // Extract expiry dates directly from GRN data
  const fetchExpiryDates = async (grnData) => {
    if (!grnData?.receivedItems || grnData.receivedItems.length === 0) {
      console.log('📦 No received items to extract expiry dates from')
      return
    }

    try {
      setExpiryLoading(true)
      const expiryMap = {}

      console.log('📦 Extracting expiry dates from GRN received items')
      
      // Extract expiry dates directly from GRN items
      grnData.receivedItems.forEach((item, index) => {
        const itemId = item._id
        const itemName = item.productSnapshot?.name || item.rawMaterialSnapshot?.name
        
        if (item.expiryDate) {
          expiryMap[itemId] = item.expiryDate
          console.log(`✅ Found expiry date for ${itemName}:`, {
            itemId,
            expiryDate: item.expiryDate
          })
        } else {
          console.log(`ℹ️ No expiry date for ${itemName} (ID: ${itemId})`)
        }
      })
      
      setExpiryDates(expiryMap)
      console.log('📅 Final expiry dates extracted:', expiryMap)
      console.log('📅 Found expiry dates for', Object.keys(expiryMap).length, 'out of', grnData.receivedItems.length, 'items')
    } catch (error) {
      console.error('❌ Error extracting expiry dates:', error)
    } finally {
      setExpiryLoading(false)
    }
  }

  // Fetch detailed GRN data
  const fetchGRNDetails = async () => {
    try {
      setLoading(true)
      
      if (grn?._id) {
        const response = await GRNService.getGRNById(grn._id)
        if (response?.success) {
          // Extract the correct data from API response structure
          const actualGRNData = response.data?.goodsReceiptNote || response.data
          console.log('📦 Extracted GRN data:', actualGRNData)
          setGRNDetails(actualGRNData)
          
          // Fetch additional branch and supplier info if they are just IDs
          await fetchAdditionalInfo(actualGRNData)
          
          // Fetch expiry dates from batch inventories
          await fetchExpiryDates(actualGRNData)
        } else {
          // Fallback to use the grn data passed directly
          console.warn('API call failed, using passed GRN data:', grn)
          setGRNDetails(grn)
          await fetchAdditionalInfo(grn)
          await fetchExpiryDates(grn)
        }
      } else {
        // Use the grn data passed directly
        console.log('Using passed GRN data directly:', grn)
        setGRNDetails(grn)
        await fetchAdditionalInfo(grn)
        await fetchExpiryDates(grn)
      }
    } catch (error) {
      console.error('❌ Error fetching GRN details:', error)
      // Fallback to use passed grn data
      console.log('Error occurred, using passed GRN data:', grn)
      setGRNDetails(grn)
      await fetchAdditionalInfo(grn)
      await fetchExpiryDates(grn)
    } finally {
      setLoading(false)
    }
  }

  // ENHANCED: Fetch additional branch and supplier information
  const fetchAdditionalInfo = async (grnData) => {
    if (!grnData?.purchaseOrder) return

    try {
      setAdditionalDataLoading(true)
      const { requestingBranch, supplier, warehouse } = grnData.purchaseOrder

      console.log('🔍 Fetching additional info for:', {
        requestingBranch,
        supplier,
        warehouse,
        supplierType: typeof supplier,
        warehouseType: typeof warehouse
      })

      const promises = []

      // Fetch branch info if it's just an ID (string)
      if (requestingBranch && typeof requestingBranch === 'string') {
        console.log('🏢 Fetching branch info for ID:', requestingBranch)
        promises.push(
          BranchService.getBranchById(requestingBranch)
            .then(response => {
              console.log('🏢 Branch API response:', response)
              if (response?.success) {
                const branchData = response.data?.branch || response.data
                setBranchInfo(branchData)
                console.log('✅ Branch info fetched:', branchData)
              }
            })
            .catch(error => {
              console.error('❌ Error fetching branch info:', error)
              setBranchInfo(null)
            })
        )
      }

      // ENHANCED: Fetch supplier/warehouse info with better logic
      const supplierId = supplier || warehouse
      if (supplierId && typeof supplierId === 'string') {
        console.log('🏭 Fetching supplier/warehouse info for ID:', supplierId)
        
        // Try supplier service first
        promises.push(
          SuppliersService.getSupplierById(supplierId)
            .then(response => {
              console.log('🏭 Supplier API response:', response)
              if (response?.success) {
                const supplierData = response.data?.supplier || response.data
                setSupplierInfo(supplierData)
                console.log('✅ Supplier info fetched:', supplierData)
              } else {
                // If supplier fetch fails, try branch service (for warehouses)
                console.log('🏭 Supplier not found, trying branch service for warehouse...')
                return BranchService.getBranchById(supplierId)
                  .then(branchResponse => {
                    console.log('🏭 Branch/Warehouse API response:', branchResponse)
                    if (branchResponse?.success) {
                      const warehouseData = branchResponse.data?.branch || branchResponse.data
                      setSupplierInfo({
                        ...warehouseData,
                        name: warehouseData.name + ' (Warehouse)',
                        isWarehouse: true
                      })
                      console.log('✅ Warehouse info fetched:', warehouseData)
                    }
                  })
              }
            })
            .catch(error => {
              console.error('❌ Error fetching supplier/warehouse info:', error)
              setSupplierInfo(null)
            })
        )
      }

      // Wait for all promises to resolve
      if (promises.length > 0) {
        await Promise.all(promises)
      }
    } catch (error) {
      console.error('❌ Error fetching additional info:', error)
    } finally {
      setAdditionalDataLoading(false)
    }
  }

  // Handle background click
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // ENHANCED: Get status configuration with proper calculation
  const getStatusConfig = (grn) => {
    console.log('📊 Calculating status for GRN:', grn.grnNumber, {
      receivedItems: grn.receivedItems,
      totalItems: grn.receivedItems?.length
    })

    // Calculate actual status based on received items
    if (!grn.receivedItems || grn.receivedItems.length === 0) {
      return {
        label: 'No Items',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200',
        icon: '⚠️'
      }
    }

    let completeItems = 0
    let partialItems = 0
    let incompleteItems = 0

    grn.receivedItems.forEach(item => {
      const ordered = parseFloat(item.orderedQuantity) || 0
      const received = parseFloat(item.receivedQuantity) || 0
      
      if (received === 0) {
        incompleteItems++
      } else if (received >= ordered) {
        completeItems++
      } else {
        partialItems++
      }
    })

    console.log('📊 Item status breakdown:', {
      completeItems,
      partialItems,
      incompleteItems,
      totalItems: grn.receivedItems.length
    })

    // Determine overall status
    if (completeItems === grn.receivedItems.length) {
      return {
        label: 'Complete',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200',
        icon: '✅'
      }
    } else if (incompleteItems === grn.receivedItems.length) {
      return {
        label: 'Incomplete',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200',
        icon: '❌'
      }
    } else {
      return {
        label: 'Partial',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200',
        icon: '⚠️'
      }
    }
  }

  // ENHANCED: Get branch display name with better fallback
  const getBranchDisplayName = () => {
    const { requestingBranch } = grnDetails?.purchaseOrder || {}
    
    console.log('🏢 Getting branch display name:', {
      requestingBranch,
      branchInfo,
      type: typeof requestingBranch
    })
    
    // If it's an object with name, use the name
    if (requestingBranch && typeof requestingBranch === 'object' && requestingBranch.name) {
      return requestingBranch.name
    }
    
    // If we fetched branch info separately, use that
    if (branchInfo?.name) {
      return branchInfo.name
    }
    
    // If it's just an ID, show loading or ID
    if (typeof requestingBranch === 'string') {
      return additionalDataLoading ? (
        <span className="flex items-center">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          Loading...
        </span>
      ) : (
        <span>
          Branch ID: {requestingBranch.slice(-8)}
          <span className="text-xs text-gray-500 ml-2">(Not found)</span>
        </span>
      )
    }
    
    return 'N/A'
  }

  // ENHANCED: Get supplier display name with better logic
  const getSupplierDisplayName = () => {
    const { supplier, warehouse } = grnDetails?.purchaseOrder || {}
    const supplierId = supplier || warehouse
    
    console.log('🏭 Getting supplier display name:', {
      supplier,
      warehouse,
      supplierId,
      supplierInfo,
      type: typeof supplierId
    })
    
    // If it's an object with name, use the name
    if (supplierId && typeof supplierId === 'object' && supplierId.name) {
      return supplierId.name
    }
    
    // If we fetched supplier info separately, use that
    if (supplierInfo?.name) {
      return supplierInfo.name
    }
    
    // If it's just an ID, show loading or ID
    if (typeof supplierId === 'string') {
      return additionalDataLoading ? (
        <span className="flex items-center">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          Loading...
        </span>
      ) : (
        <span>
          Unknown Supplier/Warehouse
          <div className="text-xs text-gray-500 mt-1">ID: {supplierId.slice(-8)}</div>
        </span>
      )
    }
    
    return 'N/A'
  }

  // ENHANCED: Calculate item statistics
  const calculateItemStats = () => {
    if (!grnDetails?.receivedItems || grnDetails.receivedItems.length === 0) {
      return {
        completeItems: 0,
        partialItems: 0,
        incompleteItems: 0,
        totalValue: 0
      }
    }

    let completeItems = 0
    let partialItems = 0
    let incompleteItems = 0
    let totalValue = 0

    grnDetails.receivedItems.forEach(item => {
      const ordered = parseFloat(item.orderedQuantity) || 0
      const received = parseFloat(item.receivedQuantity) || 0
      const totalPrice = parseFloat(item.totalPrice) || 0
      
      totalValue += totalPrice
      
      if (received === 0) {
        incompleteItems++
      } else if (received >= ordered) {
        completeItems++
      } else {
        partialItems++
      }
    })

    return {
      completeItems,
      partialItems,
      incompleteItems,
      totalValue
    }
  }

  useEffect(() => {
    console.log('🔍 ViewGRNModal received GRN data:', grn)
    fetchGRNDetails()
  }, [grn])

  if (loading) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleBackgroundClick}
      >
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex items-center justify-center">
            <OrangeLoadingSpinner size="lg" text="Loading GRN details..." />
          </div>
        </div>
      </div>
    )
  }

  if (!grnDetails) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleBackgroundClick}
      >
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">GRN Not Found</h3>
            <p className="text-gray-600 mb-4">Unable to load GRN details.</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    )
  }

  console.log('🔍 Final grnDetails being displayed:', grnDetails)

  const statusConfig = getStatusConfig(grnDetails)
  const itemStats = calculateItemStats()

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={handleBackgroundClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl my-8 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center space-x-4">
            {/* <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="h-6 w-6 text-blue-600" />
            </div> */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">GRN Details</h2>
              <p className="text-sm text-gray-600">{grnDetails.grnNumber}</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor} border ${statusConfig.borderColor}`}>
              {/* <span className="mr-1">{statusConfig.icon}</span> */}
              {statusConfig.label}
            </span>
          </div>
         <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 p-6">
            {/* Left Panel - GRN Information (2 columns) */}
            <div className="xl:col-span-2 space-y-6">
              {/* Order Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  Order Information
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">GRN Number</span>
                    <span className="font-medium text-gray-900">{grnDetails.grnNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">PO ID</span>
                    <span className="font-medium text-gray-900">{grnDetails.purchaseOrder?.poNumber || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date</span>
                    <span className="font-medium text-gray-900">{formatDate(grnDetails.receivedDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Branch</span>
                    <span className="font-medium text-gray-900">
                      {getBranchDisplayName()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Create By</span>
                    <span className="font-medium text-gray-900">{grnDetails.createdBy?.username || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* ENHANCED: Supplier/Warehouse Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Truck className="h-5 w-5 mr-2 text-green-600" />
                  Supplier/Warehouse information
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name</span>
                    <span className="font-medium text-gray-900">
                      {getSupplierDisplayName()}
                    </span>
                  </div>
                  {/* Only show ID if we couldn't resolve the name */}
                  {(!supplierInfo?.name && (grnDetails.purchaseOrder?.supplier || grnDetails.purchaseOrder?.warehouse)) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">ID</span>
                      <span className="font-mono text-xs text-gray-500">
                        {grnDetails.purchaseOrder?.supplier || grnDetails.purchaseOrder?.warehouse}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Notes</span>
                    <span className="font-medium text-gray-900">{grnDetails.note || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Received:</span>
                    <span className="text-xl font-bold text-green-600">{formatCurrency(itemStats.totalValue)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {grnDetails.note && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-purple-600" />
                    Notes
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border">
                    {grnDetails.note}
                  </p>
                </div>
              )}
            </div>

            {/* Right Panel - Item Details (3 columns for more space) */}
            <div className="xl:col-span-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="h-5 w-5 mr-2 text-orange-600" />
                  Received Items
                </h3>
                
                {/* Items Table - Scrollable when many items */}
                <div className="bg-white rounded-lg border overflow-hidden">
                  <div className="overflow-x-auto">
                    <div className="max-h-80 overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-gray-100 z-10">
                          <tr className="text-sm font-medium text-gray-700 border-b">
                            <th className="text-left py-3 px-4">Item name</th>
                            <th className="text-left py-3 px-4">Ordered</th>
                            <th className="text-left py-3 px-4">Received</th>
                            <th className="text-left py-3 px-4">Unit</th>
                            <th className="text-left py-3 px-4">Unit Price(Rs.)</th>
                            <th className="text-left py-3 px-4">Total(Rs.)</th>
                            <th className="text-left py-3 px-4">Expiry Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grnDetails.receivedItems && grnDetails.receivedItems.length > 0 ? (
                            grnDetails.receivedItems.map((item, index) => (
                              <tr key={index} className="border-b hover:bg-gray-50">
                                <td className="py-3 px-4">
                                  <div className="font-medium text-gray-900">
                                    {item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown Item'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {item.productSnapshot?.sku || item.rawMaterialSnapshot?.code || 'N/A'}
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-gray-900">{item.orderedQuantity}</td>
                                <td className="py-3 px-4">
                                  <span className={`font-medium ${
                                    item.receivedQuantity === item.orderedQuantity 
                                      ? 'text-green-600' 
                                      : item.receivedQuantity > 0 
                                        ? 'text-yellow-600' 
                                        : 'text-red-600'
                                  }`}>
                                    {item.receivedQuantity}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-gray-900">
                                  {item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs'}
                                </td>
                                <td className="py-3 px-4 text-gray-900">{(item.unitPrice || 0).toFixed(2)}</td>
                                <td className="py-3 px-4 font-medium text-gray-900">{(item.totalPrice || 0).toFixed(2)}</td>
                                <td className="py-3 px-4 text-gray-900">
                                  {(() => {
                                    const itemId = item._id
                                    const expiryDate = expiryDates[itemId]
                                    console.log(`📅 Display check for item:`, {
                                      itemId,
                                      itemName: item.productSnapshot?.name || item.rawMaterialSnapshot?.name,
                                      hasExpiryDate: !!expiryDate,
                                      expiryDate,
                                      allExpiryKeys: Object.keys(expiryDates)
                                    })
                                    
                                    if (expiryLoading) {
                                      return <span className="text-xs text-gray-400">Loading...</span>
                                    } else if (expiryDate) {
                                      return <span className="text-sm">{formatDate(expiryDate)}</span>
                                    } else {
                                      return <span className="text-gray-400 text-sm">N/A</span>
                                    }
                                  })()}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={7} className="py-8 text-center text-gray-500">
                                <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                <span>No items received</span>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* ENHANCED: Summary with correct calculations */}
                {grnDetails.receivedItems && grnDetails.receivedItems.length > 0 && (
                  <div className="mt-4 bg-blue-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-lg font-bold text-green-600">
                          {itemStats.completeItems}
                        </div>
                        <div className="text-sm text-gray-600">Complete</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-yellow-600">
                          {itemStats.partialItems}
                        </div>
                        <div className="text-sm text-gray-600">Partial</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-red-600">
                          {itemStats.incompleteItems}
                        </div>
                        <div className="text-sm text-gray-600">Not Received</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-blue-600">
                          {formatCurrency(itemStats.totalValue)}
                        </div>
                        <div className="text-sm text-gray-600">Total Value</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
          <div className="flex justify-end pt-6 pr-6 pb-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
      </div>
    </div>
  )
}

export default ViewGRNModal